import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useSEO, generateWebPageSchema, generateBreadcrumbSchema } from '../../utils/seo';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';
import ParksHero from './components/ParksHero';
import StatsBar from './components/StatsBar';
import CategoryFilter from './components/CategoryFilter';
import FeaturedParks from './components/FeaturedParks';
import TrailFinder from './components/TrailFinder';
import PlaygroundsSection from './components/PlaygroundsSection';
import SportsSection from './components/SportsSection';
import ParksGrid from './components/ParksGrid';
import ParkNewsletter from './components/ParkNewsletter';
import AmenitiesSection from './components/AmenitiesSection';
import ParksMapView from './components/ParksMapView';

export default function ParksPage() {
  const [activeCategory, setActiveCategory] = useState('All');
  const [viewMode, setViewMode] = useState<'list' | 'map'>('list');

  useSEO({
    title: 'Roseville CA Parks & Recreation - Playgrounds, Trails, Sports Fields & Activities',
    description: 'Explore parks and recreation in Roseville California. Find playgrounds, hiking trails, sports fields, picnic areas, and outdoor activities. Complete guide to Roseville parks with amenities and maps.',
    keywords: 'Roseville CA parks, parks in Roseville California, hiking trails Roseville, playgrounds Roseville, sports fields Roseville, outdoor activities Roseville, recreation Roseville CA',
    canonical: '/parks',
    ogImage: 'https://public.readdy.ai/ai/img_res/7208b03a8194631bab14e3b8a5e88edc.jpg',
    schema: {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'CollectionPage',
          name: 'Roseville Parks & Recreation',
          description: 'Comprehensive guide to parks, trails, and recreational facilities in Roseville, California',
          url: `${import.meta.env.VITE_SITE_URL || 'https://roseville.org'}/parks`,
          inLanguage: 'en-US',
          isPartOf: { '@type': 'WebSite', name: 'Roseville.org', url: import.meta.env.VITE_SITE_URL || 'https://roseville.org' },
        },
        generateBreadcrumbSchema([
          { name: 'Home', url: '/' },
          { name: 'Parks & Recreation', url: '/parks' }
        ]),
        {
          '@type': 'FAQPage',
          mainEntity: [
            {
              '@type': 'Question',
              name: 'How many parks are in Roseville CA?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: 'Roseville has over 75 parks and recreational areas covering approximately 3,800 acres of parkland. This includes neighborhood parks, community parks, sports complexes, and natural open spaces with hiking trails.'
              }
            },
            {
              '@type': 'Question',
              name: 'Are there hiking trails in Roseville California?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: 'Yes! Roseville has an extensive trail network with over 45 miles of paved and unpaved trails. Popular trails include the Dry Creek Parkway Trail, Miners Ravine Trail, and trails throughout Maidu Regional Park.'
              }
            },
            {
              '@type': 'Question',
              name: 'Does Roseville have dog parks?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: 'Yes, Roseville has several off-leash dog parks including areas at Mahany Park and other community parks. Dogs must be licensed and vaccinated. Check the City of Roseville Parks & Recreation website for current locations and rules.'
              }
            }
          ]
        }
      ]
    }
  });

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <ParksHero />
      <StatsBar />
      <CategoryFilter
        activeCategory={activeCategory}
        onCategoryChange={setActiveCategory}
        viewMode={viewMode}
        onViewModeChange={setViewMode}
      />

      {viewMode === 'map' ? (
        <ParksMapView activeCategory={activeCategory} />
      ) : (
        <>
          {(activeCategory === 'All' || activeCategory === 'Trails') && (
            <>
              <FeaturedParks />
              <TrailFinder />
            </>
          )}

          {(activeCategory === 'All' || activeCategory === 'Playgrounds') && (
            <PlaygroundsSection />
          )}

          {(activeCategory === 'All' || activeCategory === 'Sports Facilities') && (
            <SportsSection />
          )}

          {activeCategory !== 'All' && <ParksGrid category={activeCategory} />}

          {activeCategory === 'All' && <AmenitiesSection />}
        </>
      )}

      <ParkNewsletter />

      <section className="py-20 bg-gradient-to-r from-teal-600 to-emerald-600">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            Ready to Explore Roseville Outdoors?
          </h2>
          <p className="text-teal-100 mb-8 max-w-2xl mx-auto">
            Download trail maps, reserve sports facilities, or find the perfect playground for
            your family. Roseville&apos;s 3,800 acres of parkland await you.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a
              href="https://www.roseville.ca.us/government/departments/parks_recreation_libraries"
              target="_blank"
              rel="noopener noreferrer nofollow"
              className="bg-white text-teal-700 px-8 py-3 rounded-full font-semibold hover:bg-gray-100 transition-colors whitespace-nowrap cursor-pointer"
            >
              Reserve a Facility
            </a>
            <Link
              to="/recreation"
              className="bg-white/15 hover:bg-white/25 text-white px-8 py-3 rounded-full font-semibold transition-colors backdrop-blur-sm whitespace-nowrap cursor-pointer"
            >
              View Programs
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
